import {
  useContext,
  useEffect,
  useState,
} from 'react';
import { autorun } from 'mobx';
import isEqual from 'lodash.isequal';

import CoachNotificationView from '../../../../../Model/CoachNotificationView';
import InternalAssignment from '../../../../../Model/InternalAssignment';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import { AssignmentRole } from '../../../../../utils/internalAssignments';

import { summarizePoolQueue } from '../utils/poolQueue';

const EMPTY_POOL_QUEUE = summarizePoolQueue([]);

const selectCoachIds = (assignments) => new Set(assignments.docs.map(({ coach }) => coach));

/**
 * The pod the viewer works on each of their coaches, for the coaches where they hold one.
 *
 * Read from the viewer's own assignments, which are the only ones that make them accountable for a
 * single pod. The Pool Queue is deliberately not narrowed by it: pool work is shared, so it is shown
 * whole even on a coach the viewer also holds a pod on.
 */
const selectPodIdByCoachId = (assignments) => assignments.docs.reduce((byCoach, { coach, podId }) => (
  coach && podId ? byCoach.set(coach, podId) : byCoach
), new Map());

const selectMyCoachViews = (views, { pooledCoachIds, myCoachIds, canSeeUnassignedRoster }) => views
  .filter(({ id }) => !pooledCoachIds.has(id) && (canSeeUnassignedRoster || myCoachIds.has(id)))
  .sort((viewA, viewB) => ((viewA.name ?? '').localeCompare(viewB.name ?? '')));

const useDashboardCoachViews = () => {
  const [collections, setCollections] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [myCoachViews, setMyCoachViews] = useState([]);
  const [myPodIdByCoachId, setMyPodIdByCoachId] = useState(new Map());
  const [poolQueue, setPoolQueue] = useState(EMPTY_POOL_QUEUE);

  const { userId: user, isReady: isLoggedInUserReady } = useContext(LoggedInUserContext);

  const isComponentMountedRef = useComponentMounted();

  useEffect(() => {
    const init = async () => {
      try {
        const [coachViews, myAssignments, poolAssignments] = await Promise.all([
          CoachNotificationView.getAllExternalACCoachNotificationViews(),
          InternalAssignment.getAllCurrentAssignments(user, AssignmentRole.ASSISTANT_COACH),
          InternalAssignment.getAllCurrentAssignmentsByRole(AssignmentRole.POOL),
        ]);

        const fetchedViewIds = new Set(coachViews.docs.map(({ id }) => id));
        // Pool coaches the main query leaves out need their view document fetched separately. Only
        // the ones missing at load are fetched: pool membership itself stays observed below, so
        // joining or leaving the pool is picked up live for every coach the main query covers.
        const missingPooledCoachIds = [...selectCoachIds(poolAssignments)]
          .filter((coachId) => !fetchedViewIds.has(coachId));
        const pooledViewCollections = missingPooledCoachIds.length
          ? await CoachNotificationView.getByCoachIds(missingPooledCoachIds)
          : [];

        if (isComponentMountedRef.current) {
          setCollections({
            coachViews,
            myAssignments,
            poolAssignments,
            pooledViewCollections,
          });
        }
      } catch (error) {
        if (isComponentMountedRef.current) {
          setLoadError(error);
          setIsReady(true);
        }
      }
    };

    if (isLoggedInUserReady) {
      init();
    }
  }, [
    isLoggedInUserReady,
    isComponentMountedRef,
    user,
  ]);

  useEffect(() => {
    if (!collections) {
      return null;
    }

    return autorun(() => {
      const views = [...collections.coachViews.docs];
      const pooledCoachIds = selectCoachIds(collections.poolAssignments);
      const myCoachIds = selectCoachIds(collections.myAssignments);
      const canSeeUnassignedRoster = !myCoachIds.size;

      const ownViews = selectMyCoachViews(views, { pooledCoachIds, myCoachIds, canSeeUnassignedRoster });
      const podIdByCoachId = selectPodIdByCoachId(collections.myAssignments);

      if (isComponentMountedRef.current) {
        setMyCoachViews(ownViews);
        // This autorun also re-runs on roster changes, which leave the pod assignments untouched.
        // Keeping the same map lets the table hold on to its memoised columns across those.
        setMyPodIdByCoachId((currentPodIds) => (
          isEqual(currentPodIds, podIdByCoachId) ? currentPodIds : podIdByCoachId
        ));
        setIsReady(true);
      }
    });
  }, [
    collections,
    isComponentMountedRef,
  ]);

  useEffect(() => {
    if (!collections) {
      return null;
    }

    return autorun(() => {
      const { pooledViewCollections } = collections;
      const pooledCoachIds = selectCoachIds(collections.poolAssignments);
      const pooledViews = [
        ...collections.coachViews.docs,
        ...pooledViewCollections.flatMap((collection) => [...collection.docs]),
      ].filter(({ id }) => pooledCoachIds.has(id));

      if (isComponentMountedRef.current) {
        setPoolQueue(summarizePoolQueue(pooledViews));
      }
    });
  }, [
    collections,
    isComponentMountedRef,
  ]);

  return {
    myCoachViews,
    myPodIdByCoachId,
    poolCoachViews: poolQueue.orderedViews,
    poolQueueSummaries: poolQueue.summaryByCoachId,
    poolQueueStats: poolQueue.stats,
    isReady,
    loadError,
  };
};

export default useDashboardCoachViews;
